import { Avatar } from '@/components/ui/avatar';
import { Badge, Box, Flex, HStack, IconButton, Text } from '@chakra-ui/react';
import type { GoogleDrivePermission } from '@fluenceur/types';
import { Xmark } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

const I18N_PREFIX = 'integrations.googleDrive.sharing';

export type DriveMemberBadge = 'owner' | 'teammate' | 'external' | 'domain';

export interface DriveMemberRowProps {
  permission: GoogleDrivePermission;
  badge: DriveMemberBadge;
  /** true while the row is being optimistically removed */
  isRevoking?: boolean;
  onRevoke?: (permissionId: string) => void;
}

const badgeColorByKind: Record<DriveMemberBadge, string> = {
  owner: 'gray',
  teammate: 'blue',
  external: 'gray',
  domain: 'purple',
};

const DriveMemberRow = ({ permission, badge, isRevoking, onRevoke }: DriveMemberRowProps) => {
  const { t } = useTranslation();

  const isDomain = permission.type === 'domain';
  const subjectName =
    permission.displayName ||
    (isDomain && permission.domain ? permission.domain : null) ||
    permission.emailAddress ||
    '';
  const subjectDetail = isDomain ? (permission.domain ?? '') : (permission.emailAddress ?? '');

  const badgeLabel = t(`${I18N_PREFIX}.list.${badge}Badge`);

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={3}
      py={3}
      px={4}
      borderBottomWidth="1px"
      borderColor="gray.100"
      opacity={isRevoking ? 0.4 : 1}
      transition="opacity 150ms ease-in-out"
    >
      <HStack gap={3} flex="1" minWidth={0}>
        <Avatar size="sm" name={subjectName} src={permission.photoLink ?? undefined} />
        <Box minWidth={0} flex="1">
          <Text fontSize="sm" fontWeight="medium" truncate>
            {subjectName}
          </Text>
          {subjectDetail && subjectDetail !== subjectName && (
            <Text fontSize="xs" color="gray.fg" truncate>
              {subjectDetail}
            </Text>
          )}
        </Box>
      </HStack>

      <HStack gap={2}>
        {badgeLabel && (
          <Badge size="sm" colorPalette={badgeColorByKind[badge]} variant="subtle">
            {badgeLabel}
          </Badge>
        )}
        {!permission.isOwner && onRevoke && (
          <IconButton
            aria-label={t(`${I18N_PREFIX}.list.revoke`)}
            size="xs"
            variant="ghost"
            colorPalette="gray"
            disabled={isRevoking}
            onClick={() => onRevoke(permission.id)}
          >
            <Xmark />
          </IconButton>
        )}
      </HStack>
    </Flex>
  );
};

export default DriveMemberRow;
